import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, CheckCircle, ArrowLeft } from 'lucide-react';
import { MarketingHeader } from '../MarketingHeader';
import { MarketingFooter } from '../MarketingFooter';
import { SeoHead } from '../SeoHead';
import { FeatureStickyNav } from './FeatureStickyNav';

interface Step {
  num: number;
  title: string;
  desc: string;
  mockup?: ReactNode;
}

interface UseCase {
  audience: string;
  benefit: string;
}

interface FAQItem {
  q: string;
  a: string;
}

interface FeaturePageLayoutProps {
  seo: { title: string; description: string; canonical: string; keywords: string };
  activeNavId: string;
  hero: { title: string; subtitle: string; tag?: string; mockup: ReactNode };
  problem: { title: string; points: string[] };
  steps: Step[];
  screens?: ReactNode[];
  useCases: UseCase[];
  benefits: string[];
  faq: FAQItem[];
  relatedLinks?: { label: string; href: string }[];
  onNavigate?: (path: string) => void;
  onSignIn?: () => void;
  onSignUp?: () => void;
}

export function FeaturePageLayout({
  seo, activeNavId, hero, problem, steps, screens, useCases, benefits, faq, relatedLinks,
  onNavigate, onSignIn, onSignUp,
}: FeaturePageLayoutProps) {
  const navigate = useNavigate();
  const nav = onNavigate ?? ((p: string) => { window.scrollTo(0, 0); navigate(p); });
  const signIn = onSignIn ?? (() => navigate('/login'));
  const signUp = onSignUp ?? (() => navigate('/login'));

  return (
    <>
      <SeoHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
      />
      <div className="min-h-screen bg-white">
        <MarketingHeader onNavigate={nav} onSignIn={signIn} onSignUp={signUp} />
        <FeatureStickyNav activeId={activeNavId} />

        {/* breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <button onClick={() => nav('/fonctionnalites')} className="hover:text-blue-600 transition-colors flex items-center gap-1">
              <ArrowLeft size={11} />
              Fonctionnalités
            </button>
            <ChevronRight size={11} />
            <span className="text-gray-600">{hero.title}</span>
          </div>
        </div>

        {/* HERO */}
        <section className="bg-gradient-to-b from-slate-50 to-white pt-6 pb-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {hero.tag && (
                  <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mb-6">
                    {hero.tag}
                  </div>
                )}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-5 tracking-tight">
                  {hero.title}
                </h1>
                <p className="text-lg text-gray-500 mb-8 leading-relaxed">{hero.subtitle}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => nav('/beta-founders')}
                    className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl hover:bg-blue-700 active:scale-95 transition-all font-bold shadow-lg"
                  >
                    Demander une démo <ArrowRight size={18} />
                  </button>
                  <button
                    onClick={() => signUp()}
                    className="inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 px-7 py-3.5 rounded-xl hover:border-blue-300 hover:text-blue-600 transition-all font-medium"
                  >
                    Essai gratuit 14j
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-3">Sans carte bancaire · Sans engagement</p>
              </div>
              <div className="flex justify-center lg:justify-end">
                {hero.mockup}
              </div>
            </div>
          </div>
        </section>

        {/* PROBLÈME */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-7 bg-red-500 rounded-full" />
              <span className="text-sm font-semibold text-red-500 uppercase tracking-wide">Le problème</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{problem.title}</h2>
            <div className="space-y-3">
              {problem.points.map((p, i) => (
                <div key={i} className="flex items-start gap-3 bg-white border border-gray-100 rounded-xl p-4">
                  <div className="w-6 h-6 rounded-full bg-red-100 text-red-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</div>
                  <p className="text-gray-700 text-sm leading-relaxed">{p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMMENT CA MARCHE */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-1 h-7 bg-blue-600 rounded-full" />
                <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Comment ça marche</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">En quelques étapes simples</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {steps.map((step) => (
                <div key={step.num} className="flex gap-4">
                  <div className="w-9 h-9 rounded-xl bg-blue-600 text-white font-black text-sm flex items-center justify-center flex-shrink-0">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                    {step.mockup && <div className="mt-4">{step.mockup}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SCREENS */}
        {screens && screens.length > 0 && (
          <section className="py-16 px-4 bg-gray-50">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-bold text-gray-900">L'outil en images</h2>
              </div>
              <div className="flex flex-wrap justify-center gap-8">
                {screens.map((screen, i) => (
                  <div key={i} className="flex justify-center">{screen}</div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CAS D'USAGE */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Pour qui ?</h2>
              <p className="text-gray-500 text-sm">Des profils variés, un outil commun</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-5 hover:border-blue-200 hover:bg-blue-50/30 transition-all">
                  <div className="font-semibold text-gray-900 text-sm mb-1">{uc.audience}</div>
                  <p className="text-xs text-gray-500 leading-relaxed">{uc.benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BÉNÉFICES */}
        <section className="py-16 px-4 bg-blue-600">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-8">Ce que vous gagnez concrètement</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/10 rounded-xl p-4">
                  <CheckCircle size={16} className="text-green-300 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-50">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Questions fréquentes</h2>
            <div className="space-y-4">
              {faq.map((item, i) => (
                <details key={i} className="border border-gray-200 rounded-xl overflow-hidden group">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer bg-white hover:bg-gray-50 transition-colors list-none">
                    <span className="font-semibold text-gray-900 text-sm pr-4">{item.q}</span>
                    <ChevronRight size={16} className="text-gray-400 flex-shrink-0 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 pb-5 pt-2 bg-white border-t border-gray-100">
                    <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* MAILLAGE INTERNE */}
        {relatedLinks && relatedLinks.length > 0 && (
          <section className="py-12 px-4 bg-gray-50 border-t border-gray-100">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Autres fonctionnalités</h3>
              <div className="flex flex-wrap gap-2">
                {relatedLinks.map((link, i) => (
                  <button
                    key={i}
                    onClick={() => nav(link.href)}
                    className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:border-blue-300 hover:text-blue-600 transition-all bg-white"
                  >
                    {link.label} <ArrowRight size={12} />
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA FINAL */}
        <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800">
          <div className="max-w-xl mx-auto text-center text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Prêt à piloter votre trésorerie ?</h2>
            <p className="text-gray-400 mb-8 text-sm leading-relaxed">
              14 jours d'essai gratuit. Sans carte bancaire. Sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => nav('/beta-founders')}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl hover:bg-blue-700 font-bold shadow-lg transition-all"
              >
                Demander une démo <ArrowRight size={18} />
              </button>
              <button
                onClick={() => signUp()}
                className="inline-flex items-center justify-center gap-2 border border-gray-600 text-gray-300 px-7 py-3.5 rounded-xl hover:border-gray-400 hover:text-white transition-all font-medium"
              >
                Essai gratuit
              </button>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
